/**
 * FleetDashboard
 * Dashboard med kort-layout för flottarelaterade funktioner
 */

import { Link } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/store/authStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import ModuleFavoriteStar from '@/components/common/ModuleFavoriteStar';
import {
  TruckIcon,
  CurrencyDollarIcon,
  DocumentArrowUpIcon,
  ChartBarIcon,
  BoltSlashIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

const FleetDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasFeatureAccess } = usePermissions();

  const isAdmin = user?.is_global_admin === true;
  const canSee = (code: string) => isAdmin || hasFeatureAccess(code);

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      <div className="flex items-center gap-4 mb-6">
        <PageBackButton />
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <TruckIcon className="w-6 h-6 text-primary" />
          {t('nav.fleet') || 'Flotta'}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {canSee('FLEET') && (
          <Link
            to={ROUTES.FLEET_OVERVIEW}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="fleet/overview" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-BgPrimaryLight rounded-lg">
                <TruckIcon className="w-8 h-8 text-bgPrimary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('fleet.overview') || 'Fordonsflotta'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('fleet.overviewDescription') || 'Visa och hantera alla fordon'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {(canSee('UNPLUG_EVENTS') || canSee('WIDGET_UNPLUGGED')) && (
          <Link
            to={ROUTES.FLEET_UNPLUG_EVENTS}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="fleet/unplug-events" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <BoltSlashIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('unplugEvents.title')}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('unplugEvents.subtitle')}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('SYSTEM_ECONOMY') && (
          <Link
            to={ROUTES.FLEET_ECONOMY_EXPORT}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="fleet/economy-export" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <CurrencyDollarIcon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('vehicleEconomy.exportTitle') || 'Export fordonsekonomi'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('vehicleEconomy.exportDescription') || 'Underlag för bokföring och fakturering'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('COST_INVOICE_IMPORT') && (
          <Link
            to={ROUTES.FLEET_COST_INVOICES}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="fleet/cost-invoices" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-sky-100 dark:bg-sky-900/30 rounded-lg">
                <DocumentArrowUpIcon className="w-8 h-8 text-sky-600 dark:text-sky-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('costInvoice.title') || 'Kostnadsfakturaimport'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('costInvoice.cardDescription')}
                </p>
              </div>
            </div>
          </Link>
        )}

        {(canSee('VEHICLE_COST_ANALYSIS') || canSee('COST_INVOICE_IMPORT') || canSee('SYSTEM_ECONOMY')) && (
          <Link
            to={ROUTES.FLEET_VEHICLE_ECONOMY}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="fleet/vehicle-economy" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <ChartBarIcon className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('vehicleCostAnalysis.title')}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('vehicleCostAnalysis.cardDescription')}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('FLEET_PROCUREMENT') && (
          <Link
            to={ROUTES.FLEET_PROCUREMENT}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="vehicle-procurement" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                <ClipboardDocumentCheckIcon className="w-8 h-8 text-violet-600 dark:text-violet-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('procurement.title')}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('procurement.cardDescription')}
                </p>
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
};

export default FleetDashboard;
